import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, UserCheck } from 'lucide-react';
import { crm, type ContactRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onAssigned: () => void;
}

/**
 * Eine bereits vorhandene Person dieser Firma zuordnen.
 *
 * Bisher ging das nur über den Umweg Kontakt öffnen → bearbeiten → Firmenname
 * exakt tippen, und bei einer Firma ohne Domain gab es von der Firmenakte aus
 * gar keinen Weg.
 *
 * Die Liste zeigt nur Kontakte derselben Zielgruppe; Personen ohne Firma
 * stehen oben, weil das nach einem Import der Stapel ist, den man abarbeitet.
 * Hängt jemand schon an einer anderen Firma, steht das an der Zeile — ein
 * Zuordnen zieht ihn dort weg, und das soll niemand versehentlich tun.
 */
export function AssignContactDialog({
  organizationId,
  organizationName,
  onClose,
  onAssigned,
}: Props) {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<ContactRow[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const feldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    feldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const laden = useCallback(async () => {
    try {
      setTreffer(
        await crm.contacts.assignable({
          organizationId,
          ...(suche.trim() ? { search: suche.trim() } : {}),
        }),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
    setLaedt(false);
  }, [organizationId, suche]);

  // Wie auf der Kontaktseite: kurz abwarten, damit nicht jeder Tastendruck
  // eine Anfrage auslöst.
  const erster = useRef(true);
  useEffect(() => {
    if (erster.current) {
      erster.current = false;
      void laden();
      return;
    }
    const t = setTimeout(() => void laden(), 250);
    return () => clearTimeout(t);
  }, [laden]);

  const zuordnen = async (kontakt: ContactRow) => {
    setBusy(kontakt.id);
    setError(null);
    try {
      await crm.contacts.setOrganization(kontakt.id, organizationId);
      onAssigned();
      // Die Auswahl neu holen: der eben Zugeordnete gehört nicht mehr hinein,
      // und der Dialog bleibt offen, um den nächsten anzuhängen.
      await laden();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zuordnen fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Vorhandene Person zuordnen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Person zuordnen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Wer gehört zu <strong>{organizationName}</strong>? Die Liste zeigt nur
          Kontakte derselben Zielgruppe.
        </p>

        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={feldRef}
            className="pl-9"
            placeholder="Name, E-Mail oder Telefon …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            aria-label="Kontakte durchsuchen"
          />
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-3 max-h-80 overflow-y-auto scrollbar-thin rounded-md border">
          {laedt ? (
            <p className="p-4 text-sm text-muted-foreground">Lade …</p>
          ) : treffer.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {suche.trim()
                ? 'Kein Kontakt passt zur Suche.'
                : 'Kein Kontakt dieser Zielgruppe steht zur Auswahl. Über „Neue Person“ lässt sich einer anlegen.'}
            </p>
          ) : (
            <ul className="divide-y">
              {treffer.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void zuordnen(c)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{c.displayName}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[c.role, c.email, c.phone].filter(Boolean).join(' · ') || '—'}
                      </p>
                      {c.organizationName && (
                        <p className="text-xs text-amber-700">
                          hängt an {c.organizationName} — wird dort abgezogen
                        </p>
                      )}
                    </div>
                    {busy === c.id ? (
                      <span className="shrink-0 text-xs text-muted-foreground">…</span>
                    ) : (
                      <UserCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy !== null}>
            Fertig
          </Button>
        </div>
      </div>
    </div>
  );
}
